import { useState, useCallback, useEffect, useRef } from "react";
import useEmbedMode from "./useEmbedMode";

/**
 * Hook for detecting hover states and capturing underlying URLs in embed mode
 * This enables interactive embedding where users can hover over elements to see
 * what they would navigate to, then click to open in a new window
 */
const useEmbedHoverDetection = () => {
  const { isEmbedMode } = useEmbedMode();
  const [hoveredUrl, setHoveredUrl] = useState(null);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: null, y: null });
  const hoverTimeoutRef = useRef(null);

  // Function to extract URL from an element
  const extractUrlFromElement = useCallback((element) => {
    if (!element || typeof element.getAttribute !== "function") {
      return null;
    }

    try {
      // Check for href attribute
      const href = element.getAttribute("href");
      if (href) {
        return href;
      }

      // Check for data-href attribute
      const dataHref = element.getAttribute("data-href");
      if (dataHref) {
        return dataHref;
      }

      // Check for to attribute (React Router links)
      const to = element.getAttribute("to");
      if (to) {
        return to;
      }

      // Check for data-to attribute (React Router links)
      const dataTo = element.getAttribute("data-to");
      if (dataTo) {
        return dataTo;
      }

      // Check for data-url attribute
      const dataUrl = element.getAttribute("data-url");
      if (dataUrl) {
        return dataUrl;
      }

      // Check if element is inside a link
      if (typeof element.closest === "function") {
        const parentLink = element.closest("a");
        if (parentLink) {
          return extractUrlFromElement(parentLink);
        }

        // Check if element is inside a button that might be a link
        const parentButton = element.closest("button");
        if (parentButton) {
          const buttonHref =
            parentButton.getAttribute("data-href") ||
            parentButton.getAttribute("data-to") ||
            parentButton.getAttribute("data-url");
          if (buttonHref) {
            return buttonHref;
          }
        }

        // Check for React Router Link components (they often have specific classes)
        const reactLink = element.closest(
          '[class*="link"], [class*="Link"], [class*="router"]'
        );
        if (reactLink) {
          return extractUrlFromElement(reactLink);
        }
      }
    } catch (error) {
      // Silently handle any errors when accessing element properties
    }

    return null;
  }, []);

  // Function to add visual feedback to hoverable elements
  const addHoverVisualFeedback = useCallback(
    (element) => {
      if (!element || !isEmbedMode) {
        return;
      }

      try {
        // Add a subtle highlight to indicate it's clickable
        const elementToModify = element;
        elementToModify.style.transition = "all 0.2s ease-in-out";
        elementToModify.style.transform = "scale(1.02)";
        elementToModify.style.cursor = "pointer";
      } catch (error) {
        // Silently handle errors in visual feedback
      }
    },
    [isEmbedMode]
  );

  // Function to remove visual feedback from elements
  const removeHoverVisualFeedback = useCallback(
    (element) => {
      if (!element || !isEmbedMode) {
        return;
      }

      try {
        const elementToModify = element;
        elementToModify.style.transform = "";
        elementToModify.style.cursor = "";
      } catch (error) {
        // Silently handle errors in visual feedback removal
      }
    },
    [isEmbedMode]
  );

  // Function to handle mouse enter events
  const handleMouseEnter = useCallback(
    (event) => {
      if (!isEmbedMode) {
        return;
      }

      try {
        const element = event.target;
        if (!element || typeof element.getAttribute !== "function") {
          return;
        }

        // Skip if the element is part of the embed overlay
        if (element.closest("[data-embed-overlay]")) {
          return;
        }

        const url = extractUrlFromElement(element);

        if (url) {
          // Clear any existing timeout
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
          }

          // Set a small delay to avoid flickering
          hoverTimeoutRef.current = setTimeout(() => {
            setHoveredUrl(url);
            setHoveredElement(element);
            setMousePosition({ x: event.clientX, y: event.clientY });
            addHoverVisualFeedback(element);
          }, 200);
        }
      } catch (error) {
        // Silently handle any errors in mouse event handling
      }
    },
    [isEmbedMode, extractUrlFromElement, addHoverVisualFeedback]
  );

  // Function to handle mouse leave events
  const handleMouseLeave = useCallback(() => {
    if (!isEmbedMode) {
      return;
    }

    try {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      // Clear hover state after a small delay
      hoverTimeoutRef.current = setTimeout(() => {
        if (hoveredElement) {
          removeHoverVisualFeedback(hoveredElement);
        }
        setHoveredUrl(null);
        setHoveredElement(null);
        setMousePosition({ x: null, y: null });
      }, 300);
    } catch (error) {
      // Silently handle any errors in mouse event handling
    }
  }, [isEmbedMode, hoveredElement, removeHoverVisualFeedback]);

  // Function to get the URL that should be opened when clicking
  const getClickUrl = useCallback(() => {
    return hoveredUrl || window.location.href;
  }, [hoveredUrl]);

  // Function to handle click events in embed mode
  const handleEmbedClick = useCallback(
    (event) => {
      if (!isEmbedMode) {
        return;
      }

      const url = getClickUrl();
      if (url && url !== window.location.href) {
        event.preventDefault();
        event.stopPropagation();

        // Import the utility function dynamically to avoid circular dependencies
        // eslint-disable-next-line consistent-return
        import("src/util/embedUtils").then(({ getCleanUrlForNewWindow }) => {
          const cleanUrl = getCleanUrlForNewWindow(url);
          window.open(cleanUrl, "_blank", "noopener,noreferrer");
        });
      }
    },
    [isEmbedMode, getClickUrl]
  );

  // Add global event listeners when in embed mode
  useEffect(() => {
    if (!isEmbedMode) {
      setHoveredUrl(null);
      setHoveredElement(null);
      return;
    }

    // Add event listeners to the document with error handling
    const safeMouseEnter = (event) => {
      try {
        handleMouseEnter(event);
      } catch (error) {
        // Silently handle mouse enter errors
      }
    };

    const safeMouseLeave = (event) => {
      try {
        handleMouseLeave(event);
      } catch (error) {
        // Silently handle mouse leave errors
      }
    };

    document.addEventListener("mouseenter", safeMouseEnter, true);
    document.addEventListener("mouseleave", safeMouseLeave, true);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener("mouseenter", safeMouseEnter, true);
      document.removeEventListener("mouseleave", safeMouseLeave, true);

      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [isEmbedMode, handleMouseEnter, handleMouseLeave]);

  return {
    hoveredUrl,
    hoveredElement,
    isHovering: !!hoveredUrl,
    mousePosition,
    handleEmbedClick,
    getClickUrl,
  };
};

export default useEmbedHoverDetection;
